import React, { useEffect, useState } from "react";
import { Zap, Download, RefreshCw, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const CATEGORY_ACCENTS = {
  Batting: "#FF3B30",
  Bowling: "#007AFF",
  Fielding: "#34C759",
  Fitness: "#F59E0B",
};

const PRIORITY_BADGE = {
  High: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/40",
  Medium: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/40",
  Low: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
};

export default function AICoach() {
  const [report, setReport] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get("/ai-coach/latest");
        if (data?.report) {
          setReport(data.report);
          setGeneratedAt(data.generated_at);
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const generate = async () => {
    setError("");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai-coach/report");
      setReport(data);
      setGeneratedAt(data.generated_at);
      toast.success("Coach report generated");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-gradient-to-r from-[#141414] via-[#1A1A1A] to-[#141414] border border-[#FF3B30]/30 rounded-2xl p-8 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF3B30]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-4 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-yellow-400/20 text-yellow-400 text-xs font-mono font-bold px-3 py-1 rounded border border-yellow-400/30 uppercase flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> AI Coach • Gemini 3.6 Flash
            </span>
            {generatedAt && (
              <span className="text-xs text-[#A3A3A3] font-mono">
                • Generated {new Date(generatedAt).toLocaleString()}
              </span>
            )}
          </div>
          <h3 className="text-3xl md:text-4xl font-black uppercase text-white">
            Personalised Performance & Training Dossier
          </h3>
          <p className="text-[#A3A3A3] text-sm md:text-base leading-relaxed">
            Synthesised from your logged matches — strengths, vulnerabilities and precise drill
            prescriptions generated by a national-level cricket coach reasoning model.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              data-testid="generate-report-btn"
              onClick={generate}
              disabled={generating}
              className="bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white text-xs font-bold px-5 py-2.5 rounded transition-all flex items-center gap-2 shadow-lg"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Analysing...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> {report ? "Regenerate Report" : "Generate Report"}</>
              )}
            </button>
            {report && (
              <button
                data-testid="export-report-btn"
                onClick={() => window.print()}
                className="bg-[#1A1A1A] hover:bg-[#262626] text-white text-xs font-bold px-5 py-2.5 rounded border border-[#262626] flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Export
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="ai-coach-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-sm rounded px-4 py-3">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center text-sm text-[#A3A3A3] py-10 font-mono">Loading latest report...</div>
      )}

      {!loading && !report && (
        <div className="text-center text-sm text-[#A3A3A3] bg-[#141414] border border-dashed border-[#262626] rounded-xl p-10 space-y-3">
          <p>No AI Coach report yet.</p>
          <p className="text-xs">Log at least one match, then generate your first coaching dossier.</p>
        </div>
      )}

      {report && (
        <>
          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6" data-testid="report-summary">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-mono uppercase tracking-widest text-yellow-400">Overall Assessment</span>
              <span className="text-xs bg-[#262626] text-white px-2 py-0.5 rounded font-mono">
                Form Rating: {report.form_rating ?? "—"}/100
              </span>
            </div>
            <p className="text-base text-white leading-relaxed">{report.summary}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ReportList
              testid="report-strengths"
              title="Key Technical Strengths"
              subtitle="Core proficiencies identified from your logs"
              icon={<CheckCircle2 className="w-6 h-6" />}
              accent="emerald"
              items={report.strengths || []}
              badge="text-emerald-400"
            />
            <ReportList
              testid="report-weaknesses"
              title="Identified Weaknesses"
              subtitle="Tactical risks requiring targeted practice"
              icon={<AlertTriangle className="w-6 h-6" />}
              accent="red"
              items={report.weaknesses || []}
              badge="text-[#FF3B30]"
            />
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-6" data-testid="report-recommendations">
            <div className="flex items-center justify-between border-b border-[#262626] pb-4">
              <div>
                <h4 className="text-xl font-bold uppercase text-white">Training Prescriptions</h4>
                <p className="text-xs text-[#A3A3A3]">Custom drills to elevate your performance</p>
              </div>
              <span className="bg-[#FF3B30] text-white text-xs font-bold px-3 py-1 rounded">
                {(report.training_recommendations || []).length} Drills
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(report.training_recommendations || []).map((r, i) => (
                <div key={i} className="bg-[#1A1A1A] border border-[#262626] rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span
                      className="text-xs font-mono font-bold uppercase"
                      style={{ color: CATEGORY_ACCENTS[r.category] || "#FFFFFF" }}
                    >
                      Drill {String(i + 1).padStart(2, "0")} • {r.category}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${PRIORITY_BADGE[r.priority] || "bg-[#262626] text-white border-[#333]"}`}>
                      {r.priority}
                    </span>
                  </div>
                  <h5 className="text-base font-bold text-white">{r.title}</h5>
                  <p className="text-xs text-[#A3A3A3] leading-relaxed">{r.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ReportList({ title, subtitle, icon, accent, items, badge, testid }) {
  const border = accent === "emerald" ? "border-emerald-500/30" : "border-[#FF3B30]/30";
  const bg = accent === "emerald" ? "bg-emerald-500/20 text-emerald-400" : "bg-[#FF3B30]/20 text-[#FF3B30]";
  return (
    <div data-testid={testid} className={`bg-[#141414] border ${border} rounded-xl p-6 space-y-5`}>
      <div className="flex items-center gap-3 border-b border-[#262626] pb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${bg}`}>{icon}</div>
        <div>
          <h4 className="text-lg font-bold text-white uppercase">{title}</h4>
          <p className="text-xs text-[#A3A3A3]">{subtitle}</p>
        </div>
      </div>
      <ul className="space-y-3 text-sm text-[#A3A3A3]">
        {items.length === 0 && <li className="text-xs italic">No items available.</li>}
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className={`${badge} font-bold`}>{String(i + 1).padStart(2, "0")}.</span>
            <div>
              <strong className="text-white block">{it.title}</strong>
              <span>{it.detail}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
